import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { Filter, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { JobCard } from "@/components/jobs/JobCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import {
  BrutalSelect,
  BrutalSelectTrigger,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { BrutalMultiSelect } from "@/components/ui/BrutalMultiSelect";
import { useJobsSearch, JobsSearchFilters } from "@/hooks/useJobsSearch";
import { useJobCities } from "@/hooks/useJobsSearch";
import { contractTypes, workModes, inclusivityTags } from "@/data/jobOptions";

const Jobs = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState<JobsSearchFilters>({
    page: 1,
    limit: 12,
    sort: 'recent',
    search: searchParams.get('search') || undefined
  });

  // Sync URL search param with filters
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch && urlSearch !== filters.search) {
      setFilters(prev => ({ ...prev, search: urlSearch, page: 1 }));
    }
  }, [searchParams]);

  const { data, isLoading, isFetching } = useJobsSearch(filters);
  const { data: cities = [] } = useJobCities();

  const hasActiveFilters = filters.search || filters.city || filters.contract_type || 
    filters.work_mode || (filters.inclusivity_tags?.length ?? 0) > 0;

  const handleInclusivityTagToggle = (tagValue: string) => {
    const current = filters.inclusivity_tags || [];
    const updated = current.includes(tagValue)
      ? current.filter(t => t !== tagValue)
      : [...current, tagValue];
    setFilters(prev => ({ ...prev, inclusivity_tags: updated.length > 0 ? updated : undefined, page: 1 }));
  };

  const handleFilterChange = (key: keyof JobsSearchFilters, value: string | undefined) => {
    const newValue = value === 'all' ? undefined : value;
    setFilters(prev => ({ ...prev, [key]: newValue, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={t("jobs.pageTitle")}
        description={t("jobs.pageDescription")}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <SEOBreadcrumb items={[{ label: t('common.jobs') }]} />
            <h1 className="font-heading font-bold text-4xl md:text-6xl text-dark mb-4 flex items-center justify-center gap-3">
              {t('jobs.heroTitle')}
              {isFetching && !isLoading && (
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              )}
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('jobs.heroSubtitle')}
            </p>
          </div>

          {/* Mobile Filter Button */}
          <div className="mb-4 md:hidden">
            <BrutalButton
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              variant="outline"
              className="w-full"
            >
              <Filter className="w-4 h-4 mr-2" />
              {t('jobs.showFilters')}
            </BrutalButton>
          </div>

          {/* Filters */}
          <div className={`grid md:grid-cols-6 gap-4 mb-8 ${showMobileFilters ? 'grid' : 'hidden md:grid'}`}>
            <BrutalInput
              placeholder={t('jobs.searchPlaceholder')}
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
            />
            <BrutalSelect 
              value={filters.city || 'all'} 
              onValueChange={(v) => handleFilterChange('city', v)}
            >
              <BrutalSelectTrigger>
                <BrutalSelectValue placeholder={t('jobs.filters.allCities')} />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                <BrutalSelectItem value="all">{t('jobs.filters.allCities')}</BrutalSelectItem>
                {cities.map((city) => (
                  <BrutalSelectItem key={city} value={city}>{city}</BrutalSelectItem>
                ))}
              </BrutalSelectContent>
            </BrutalSelect>
            <BrutalSelect 
              value={filters.contract_type || 'all'} 
              onValueChange={(v) => handleFilterChange('contract_type', v)}
            >
              <BrutalSelectTrigger>
                <BrutalSelectValue placeholder={t('jobs.filters.allContracts')} />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                <BrutalSelectItem value="all">{t('jobs.filters.allContracts')}</BrutalSelectItem>
                {contractTypes.map((ct) => (
                  <BrutalSelectItem key={ct.value} value={ct.value}>
                    {t(ct.labelKey)}
                  </BrutalSelectItem>
                ))}
              </BrutalSelectContent>
            </BrutalSelect>
            <BrutalSelect 
              value={filters.work_mode || 'all'} 
              onValueChange={(v) => handleFilterChange('work_mode', v)}
            >
              <BrutalSelectTrigger>
                <BrutalSelectValue placeholder={t('jobs.filters.allModes')} />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                <BrutalSelectItem value="all">{t('jobs.filters.allModes')}</BrutalSelectItem>
                {workModes.map((wm) => (
                  <BrutalSelectItem key={wm.value} value={wm.value}>
                    {t(wm.labelKey)}
                  </BrutalSelectItem>
                ))}
              </BrutalSelectContent>
            </BrutalSelect>
            <BrutalMultiSelect
              options={inclusivityTags.map(tag => ({
                value: tag.value,
                label: t(tag.labelKey),
                icon: tag.icon
              }))}
              selected={filters.inclusivity_tags || []}
              onChange={(selected) => setFilters(prev => ({ 
                ...prev, 
                inclusivity_tags: selected.length > 0 ? selected : undefined, 
                page: 1 
              }))}
              placeholder={t('jobs.filters.inclusivity')}
              searchPlaceholder={t('common.search')}
              emptyMessage={t('common.noResults')}
            />
            <BrutalSelect 
              value={filters.sort || 'recent'} 
              onValueChange={(v) => setFilters(prev => ({ ...prev, sort: v as 'recent' | 'salary_asc' | 'salary_desc', page: 1 }))}
            >
              <BrutalSelectTrigger>
                <BrutalSelectValue placeholder={t('jobs.sortBy')} />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                <BrutalSelectItem value="recent">{t('jobs.sortRecent')}</BrutalSelectItem>
                <BrutalSelectItem value="salary_desc">{t('jobs.sortSalaryDesc')}</BrutalSelectItem>
                <BrutalSelectItem value="salary_asc">{t('jobs.sortSalaryAsc')}</BrutalSelectItem>
              </BrutalSelectContent>
            </BrutalSelect>
          </div>


          {/* Results Count */}
          {data && (
            <p className="text-muted-foreground mb-6">
              {t('jobs.resultsCount', { count: data.totalCount })}
            </p>
          )}

          {/* Jobs Grid */}
          {isLoading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i} 
                  className="h-64 border-[3px] border-dark bg-muted animate-pulse shadow-[6px_6px_0px_0px] shadow-dark"
                />
              ))}
            </div>
          ) : data?.jobs && data.jobs.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {data.jobs.map((job) => (
                <Link key={job.id} to={`/jobs/${job.slug}`}>
                  <JobCard job={job} />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-dark/70 font-body text-lg">{t('jobs.noResults')}</p>
              {hasActiveFilters && (
                <BrutalButton 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => setFilters({ page: 1, limit: 12, sort: 'recent' })}
                >
                  {t('jobs.filters.reset')}
                </BrutalButton>
              )}
            </div>
          )}

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <BrutalButton
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(data.currentPage - 1)}
                disabled={data.currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                {t("common.previous")}
              </BrutalButton>

              <span className="font-heading font-bold text-dark">
                {data.currentPage} / {data.totalPages}
              </span>

              <BrutalButton
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(data.currentPage + 1)}
                disabled={data.currentPage === data.totalPages}
              >
                {t("common.next")}
                <ChevronRight className="w-4 h-4" />
              </BrutalButton>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Jobs;
